import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Chart,
  BarController,
  LineController,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";

Chart.register(
  BarController,
  LineController,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
);

// ─── chart colors (shared by Chart.js + legend) ─────────────────────────────

const chartPalette = {
  amount: "#5CC7C1",
  qty: "#5C74C7",
  pointBorder: "#fff",
  tooltipBg: "#fff",
  tooltipBorder: "#e0e0e0",
  tooltipTitle: "#333",
  tooltipBody: "#555",
  axisMuted: "#999",
  gridLine: "rgba(0,0,0,0.06)",
};

// ─── styles (module-level) ─────────────────────────────────────────────────

const styles = {
  outerWrap: {
    background: "#fff",
    padding: "0 20px 16px",
    fontFamily: "Segoe UI",
  },
  card: {
    background: "#fff",
    borderRadius: 12,
    border: "1px solid #f0f0f0",
    padding: "20px 24px",
    boxSizing: "border-box",
    width: "50%",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    color: "#606060",
    fontFamily: "Segoe UI",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  menuButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#606060",
    fontSize: 22,
    padding: 0,
  },
  menuWrapper: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dots: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#606060",
    fontSize: 22,
    lineHeight: 1,
    padding: 0,
    userSelect: "none",
  },
  menu: {
    position: "absolute",
    top: "calc(100% + 12px)",
    right: 0,
    background: "#fff",
    border: "1px solid #e9e9e9",
    borderRadius: 8,
    boxShadow: "0 6px 18px rgba(0, 0, 0, 0.12)",
    padding: "8px 12px",
    zIndex: 50,
    minWidth: 110,
  },
  menuIconRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  },
  menuIconBtn: {
    width: 34,
    height: 34,
    border: "none",
    background: "transparent",
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#666",
    padding: 0,
  },
  chartArea: {
    position: "relative",
    width: "100%",
    height: 365,
  },
  legendRoot: {
    display: "flex",
    gap: 20,
    justifyContent: "center",
    fontSize: 12,
    color: "#888",
    marginTop: 10,
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  legendSwatch: {
    width: 10,
    height: 10,
    borderRadius: 2,
    background: chartPalette.amount,
    display: "inline-block",
  },
  topNWrapper: {
    position: "relative",
    fontFamily: "Segoe UI",
  },
  topNBtn: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
    color: "#606060",
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: "2px 8px",
    cursor: "pointer",
    background: "#fff",
    whiteSpace: "nowrap",
    userSelect: "none",
    fontFamily: "Segoe UI",
  },
  topNChevron: {
    fontSize: 14,
    color: "#606060",
  },
  topNMenu: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 4px)",
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: 8,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    width: 130,
    zIndex: 20,
    overflow: "hidden",
    fontFamily: "Segoe UI",
  },
  topNOptionRowActive: {
    display: "flex",
    alignItems: "center",
    padding: "9px 14px",
    fontSize: 14,
    cursor: "pointer",
    color: "#606060",
    fontFamily: "Segoe UI",
    gap: 4,
    background: "#eef2f3",
    fontWeight: 600,
  },
  topNOptionRowInactive: {
    display: "flex",
    alignItems: "center",
    padding: "9px 14px",
    fontSize: 14,
    cursor: "pointer",
    color: "#606060",
    fontFamily: "Segoe UI",
    gap: 4,
    background: "#fff",
    fontWeight: 400,
  },
  topNCheckmark: {
    color: chartPalette.amount,
    marginLeft: "auto",
  },
};

// ─── helpers ────────────────────────────────────────────────────────────────

function fmtAmount(v) {
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + "M";
  if (v >= 1_000) return Math.round(v / 1_000) + "k";
  return String(v);
}

function wrapTickLabel(label, maxCharsPerLine = 10, maxLines = 3) {
  const text = String(label || "").trim();
  if (!text) return "";
  if (text.length <= maxCharsPerLine) return text;

  const words = text.split(/\s+/);
  const lines = [];
  let current = "";

  for (const word of words) {
    if (!current) {
      current = word;
      continue;
    }
    if (`${current} ${word}`.length <= maxCharsPerLine) {
      current = `${current} ${word}`;
    } else {
      lines.push(current);
      current = word;
      if (lines.length >= maxLines - 1) break;
    }
  }

  if (lines.length < maxLines && current) lines.push(current);
  if (lines.length >= maxLines && words.join(" ").length > lines.join(" ").length) {
    lines[maxLines - 1] = `${lines[maxLines - 1]}...`;
  }
  return lines;
}

function ThreeDotsMenu({ chartRef }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const handlePrint = () => {
    window.print();
    setOpen(false);
  };
  const handleDownload = () => {
    const chart = chartRef?.current;
    if (!chart) return;
    const url = chart.toBase64Image();
    const link = document.createElement("a");
    link.href = url;
    link.download = "chart.png";
    link.click();
    setOpen(false);
  };

  return (
    <div style={styles.menuWrapper} ref={ref}>
      <button
        style={styles.dots}
        onClick={() => setOpen(!open)}
        type="button"
        title="More options"
      >
        ⋮
      </button>
      {open && (
        <div style={styles.menu}>
          <div style={styles.menuIconRow}>
            <button
              style={styles.menuIconBtn}
              onClick={handlePrint}
              title="Print"
              type="button"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#555"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
            </button>
            <button
              style={styles.menuIconBtn}
              onClick={handleDownload}
              title="Download"
              type="button"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#555"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
                <path d="M19 16 v3 M17.5 19 h3" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function sliceRowsByTopAmount(rows, topN) {
  const n = parseInt(topN, 10);
  if (isNaN(n) || n <= 0) return rows;
  return [...rows]
    .sort(
      (a, b) => (Number(b.sellingPrice) || 0) - (Number(a.sellingPrice) || 0),
    )
    .slice(0, n);
}

function TopNDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const choices = [10, 20, 30].map((n) => ({
    key: String(n),
    label: `Top ${n}`,
  }));
  const selected = choices.find((c) => c.key === value) || choices[0];

  return (
    <div style={styles.topNWrapper} ref={ref}>
      <div style={styles.topNBtn} onClick={() => setOpen(!open)}>
        {selected.label}
        <span style={styles.topNChevron}>▾</span>
      </div>
      {open && (
        <div style={styles.topNMenu}>
          {choices.map((c) => (
            <div
              key={c.key}
              style={
                value === c.key
                  ? styles.topNOptionRowActive
                  : styles.topNOptionRowInactive
              }
              onClick={() => {
                onChange(c.key);
                setOpen(false);
              }}
            >
              {c.label}
              {value === c.key && <span style={styles.topNCheckmark}>✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Legend strip ────────────────────────────────────────────────────────────

function ChartLegend() {
  return (
    <div style={styles.legendRoot}>
      <span style={styles.legendItem}>
        <svg width="22" height="12" viewBox="0 0 22 12">
          <line
            x1="0"
            y1="6"
            x2="22"
            y2="6"
            stroke={chartPalette.qty}
            strokeWidth="2"
          />
          <circle cx="11" cy="6" r="4" fill={chartPalette.qty} />
        </svg>
        Qty
      </span>
      <span style={styles.legendItem}>
        <span style={styles.legendSwatch} />
        Amount
      </span>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

const MetalChart = ({ rows = [], text = "" }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const [topN, setTopN] = useState("10");

  const displayRows = useMemo(
    () => sliceRowsByTopAmount(rows, topN),
    [rows, topN],
  );

  useEffect(() => {
    if (!canvasRef.current || displayRows.length === 0) return;

    if (chartRef.current) {
      chartRef.current.destroy();
    }
    let labels;
    if (text === "Metal") {
      labels = displayRows.map((r) => r.metal_name);
    } else if (text === "Stone") {
      labels = displayRows.map((r) => r.stone_name);
    } else if (text === "Collection") {
      labels = displayRows.map((r) => r.collection);
    }
    else {
      labels = displayRows.map((r) => r.design_no);  
    }
    const qtyData = displayRows.map((r) => r.qty);
    const amountData = displayRows.map((r) => r.sellingPrice);

    chartRef.current = new Chart(canvasRef.current, {
      data: {
        labels,
        datasets: [
          {
            type: "bar",
            label: "Amount",
            data: amountData,
            backgroundColor: chartPalette.amount,
            borderColor: chartPalette.amount,
            borderWidth: 0,
            borderRadius: 3,
            categoryPercentage: 0.5,
            barPercentage: 0.6,
            maxBarThickness: 56,
            yAxisID: "yAmount",
            order: 2,
          },
          {
            type: "line",
            label: "Qty",
            data: qtyData,
            borderColor: chartPalette.qty,
            backgroundColor: chartPalette.qty,
            pointBackgroundColor: chartPalette.qty,
            pointBorderColor: chartPalette.pointBorder,
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 7,
            borderWidth: 2,
            tension: 0,
            fill: false,
            yAxisID: "yQty",
            order: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "index",
          intersect: false,
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: chartPalette.tooltipBg,
            borderColor: chartPalette.tooltipBorder,
            borderWidth: 1,
            titleColor: chartPalette.tooltipTitle,
            bodyColor: chartPalette.tooltipBody,
            padding: 12,
            callbacks: {
              title: (items) => items[0]?.label ?? "",
              label: (ctx) => {
                if (ctx.dataset.label === "Amount") {
                  return `  Amount : ${ctx.raw.toLocaleString()}`;
                }
                return `  Qty : ${ctx.raw}`;
              },
              labelColor: (ctx) => ({
                borderColor:
                  ctx.dataset.label === "Amount"
                    ? chartPalette.amount
                    : chartPalette.qty,
                backgroundColor:
                  ctx.dataset.label === "Amount"
                    ? chartPalette.amount
                    : chartPalette.qty,
                borderRadius: 3,
              }),
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: chartPalette.axisMuted,
              font: { size: 12 },
              autoSkip: false,
              maxRotation: 0,
              minRotation: 0,
              padding: 8,
              callback: function (_value, index) {
                const label = this.getLabelForValue(index);
                return wrapTickLabel(label, 10, 3);
              },
            },
          },
          yQty: {
            position: "left",
            title: {
              display: true,
              text: "Qty",
              color: chartPalette.axisMuted,
              font: { size: 12 },
            },
            ticks: { color: chartPalette.axisMuted, font: { size: 11 } },
            grid: { color: chartPalette.gridLine },
          },
          yAmount: {
            position: "right",
            title: {
              display: true,
              text: "Amount",
              color: chartPalette.axisMuted,
              font: { size: 12 },
            },
            ticks: {
              color: chartPalette.axisMuted,
              font: { size: 11 },
              callback: (v) => fmtAmount(v),
            },
            grid: { drawOnChartArea: false },
          },
        },
      },
    });

    return () => {
      chartRef.current?.destroy();
    };
  }, [displayRows, text]);

  return (
    <div style={styles.outerWrap}>
      <div style={styles.card}>
        <div style={styles.headerRow}>
          <span style={styles.title}>Sales by {text}</span>
          <div style={styles.headerActions}>
            <TopNDropdown value={topN} onChange={setTopN} />
            <ThreeDotsMenu chartRef={chartRef} />
          </div>
        </div>

        <div style={styles.chartArea}>
          <canvas
            ref={canvasRef}
            role="img"
            aria-label="Sales by Metal: dual-axis combo chart showing quantity as a line and sales amount as bars for each metal type."
          >
            {displayRows
              .map(
                (r) =>
                  `${r.metal_name}: qty ${r.qty}, amount ${r.sellingPrice}`,
              )
              .join("; ")}
          </canvas>
        </div>

        <ChartLegend />
      </div>
    </div>
  );
};

export default MetalChart;
